import { z } from 'zod/v4';

import {
    advancedBorderSettingsSchema,
    advancedRadiusSchema,
    analyticsSettingsPluginShapeLegacy,
    buttonAdditionalEffectsSchema,
    buttonHorizontalPositionSchema,
    buttonIconSchema,
    buttonSizeSchema,
    buttonStyleTypeSchema,
    buttonWidthTypeSchema,
    createAnalyticsSettingsLegacy,
    createThemeButtonDataLegacy,
    createThemeButtonHoverLegacy,
    shadowPluginShapeLegacy,
    themeButtonDataSchemaLegacy,
    themeButtonHoverSchemaLegacy,
} from '../../../common/schemas/legacy';

/**
 * The button-item field schemas (style/width/size/alignment enums, icon, effects,
 * advanced radius/border) now live in `common/schemas/legacy/button-settings`, shared with the
 * form/payment-form widgets — including their `.describe()` metadata. Re-exported here so existing
 * importers of `./item` (the button `schemas/index.ts` barrel and `./data`) keep working
 * unchanged.
 */
export {
    buttonStyleTypeSchema,
    buttonWidthTypeSchema,
    buttonSizeSchema,
    buttonHorizontalPositionSchema,
    buttonIconSchema,
    buttonAdditionalEffectsSchema,
    advancedRadiusSchema,
    advancedBorderSettingsSchema,
};

/**
 * Link kind, mirroring `EButtonLinkType`. Kept local — only the button item uses it (not shared
 * with the form/payment-form widgets like the schemas above).
 */
export const buttonLinkTypeSchema = z.enum(['email', 'phone', 'link', 'anchor']);

/**
 * One button of the widget's `buttons` array — matches the FormGroup
 * produced by `getButtonItemFormGroup` in the host app's button form factory.
 *
 * Liberally `nullish()`: the widget pre-dates most of these settings
 * (icons, effects, shadows, analytics, advanced radius/borders were all
 * added over time), so persisted items from older sites omit whole groups.
 * Only the fields present since the widget's introduction are required.
 */
export const buttonItemSchema = z.object({
    label: z.string().nullable().describe('Button text shown to the user.'),
    description: z.string().nullish(),
    styleType: buttonStyleTypeSchema,
    size: buttonSizeSchema,
    widthType: buttonWidthTypeSchema.nullish().describe("Sizing strategy: 'auto' | 'full' | 'fixed'."),
    widthValue: z.number().nullish().describe("Fixed width in pixels, used when widthType is 'fixed'."),
    link: z
        .object({
            type: buttonLinkTypeSchema,
            url: z.string().nullable(),
        })
        .describe('Where the button links to.'),
    isTargetBlank: z.boolean().nullish(),
    preventLinkConverting: z.boolean().nullish(),
    buttonBase: themeButtonDataSchemaLegacy,
    buttonHover: themeButtonHoverSchemaLegacy
        .nullish()
        .describe("Styling applied on hover — only rendered when styleType is 'custom'."),
    buttonActive: themeButtonDataSchemaLegacy
        .nullish()
        .describe("Styling applied while pressed/active — only rendered when styleType is 'custom'."),
    buttonIcon: buttonIconSchema.nullish().describe('Inline icon settings (isActive, icon SVG, position).'),
    elementId: z.string().nullish(),
    isIndependentCorners: z.boolean().nullish(),
    additionalEffects: buttonAdditionalEffectsSchema.nullish().describe('Hover/static animation effect settings.'),
    advancedRadius: advancedRadiusSchema
        .nullish()
        .describe("Per-corner border-radius overrides — only rendered when styleType is 'custom'."),
    advancedBorderSettings: advancedBorderSettingsSchema
        .nullish()
        .describe("Per-side border overrides — only rendered when styleType is 'custom'."),
    ...analyticsSettingsPluginShapeLegacy,
    ...shadowPluginShapeLegacy,
});

export type ButtonItem = z.infer<typeof buttonItemSchema>;

/**
 * Default button item. Mirrors `getButtonItemFormGroup()` defaults in the
 * host app — except `elementId` (host generates a cuid2 per item) and
 * `buttonIcon.icon` (host seeds an SVG from `buttonIconTemplates`); callers
 * pass those via `overrides` since this package stays dependency-free.
 */
export const createButtonItem = (overrides: Partial<ButtonItem> = {}): ButtonItem => ({
    label: 'Visit',
    description: null,
    styleType: 'primary',
    size: 'M',
    widthType: 'auto',
    widthValue: 260,
    link: {
        type: 'link',
        url: null,
    },
    isTargetBlank: false,
    preventLinkConverting: false,
    buttonBase: createThemeButtonDataLegacy(),
    buttonHover: createThemeButtonHoverLegacy(),
    buttonActive: null,
    buttonIcon: {
        isActive: false,
        icon: '',
        position: 'L',
    },
    elementId: '',
    isIndependentCorners: false,
    additionalEffects: {
        isActive: false,
        effectCondition: 'hover',
        hoverEffects: 'bounce',
        staticEffects: 'shine',
    },
    analyticsSettings: createAnalyticsSettingsLegacy(),
    advancedRadius: {
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
    },
    advancedBorderSettings: null,
    isShadow: false,
    shadowSettings: null,
    ...overrides,
});
